import Link from 'next/link';
import { type ComponentPropsWithoutRef, type MouseEvent, memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useTrackSidebarInteraction } from '@air/analytics';
import { TriangleDown } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { type DropdownMenuProps } from '@air/primitive-dropdown-menu';
import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { type Board } from '@air/types';

import { type DragType, type GetBoardDragItem, type RearrangableItem } from '~/components/Shared/Drag/dragTypes';
import { DNDBoardDrag } from '~/components/v2/components/shared/dnd/DNDBoardDrag';
import { DNDBoardDrop, type DNDBoardDropProps } from '~/components/v2/components/shared/dnd/DNDBoardDrop';
import { WorkspaceSidebarBoardsList } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsList';
import { WorkspacesSidebarBoardsListItemActions } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsListItemActions';
import { WorkspaceSidebarBoardsListItemButton } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsListItemButton';
import { WorkspaceSidebarBoardsListItemRenameInput } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsListItemRenameInput';
import { useWorkspaceSidebarSectionStore } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { usePrivateBoardLinkClick } from '~/components/v2/hooks/usePrivateBoardLinkClick';
import { useWorkspaceSidebarPushLock } from '~/components/v2/hooks/useWorkspaceSidebarPushLock';
import { createBoardRoute } from '~/constants/routes';
import { useBoardPermissions } from '~/hooks/useBoardPermissions';
import { useCreateSubBoard } from '~/hooks/useCreateSubBoard';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { isAncestorOfCentralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { useSubBoardsList } from '~/swr-hooks/boards/useSubBoardsList';
import {
  canChangeBoardAssetsOrBoardsPosition,
  canCreateBoard,
  canDeleteBoard,
} from '~/utils/permissions/boardPermissions';
import { useAirSelector } from '~/utils/ReduxUtils';

const WORKSPACE_SIDEBAR_BOARD_ITEM_DEPTH_MULTIPLIER = 12;

const WORKSPACE_SIDEBAR_BOARD_ITEM_BASE_PADDING_LEFT = 12;

const WORKSPACE_SIDEBAR_BOARD_ITEM_EXPAND_BUTTON_SIZE = 16;

const WORKSPACE_SIDEBAR_BOARD_ITEM_GAP = 8;

export type WorkspaceSidebarBoardsListItemProps = ComponentPropsWithoutRef<'div'> &
  Pick<DNDBoardDropProps, 'supportedBoardDragTypes'> & {
    /**
     * If true, the board will automatically expand when the user is
     * in the ancestor of the board.
     */
    autoExpand?: boolean;
    board: Board;
    depth: number;
    /**
     * Drag and drop type that dictates what is draggable onto the board
     */
    dragType: DragType.boardNavWorkspace | DragType.boardNavFavorites | DragType.boardNavLibrary;
    draggable?: boolean;
    droppable?: boolean;
    index: number;
    onBoardRearrange: (props: {
      adjacentItem: RearrangableItem;
      parentBoardId: Board['id'] | null;
      boards: Board[];
      board: GetBoardDragItem;
    }) => void;
  };

export const WorkspaceSidebarBoardsListItem = memo(
  ({
    autoExpand = false,
    board,
    depth,
    draggable = true,
    dragType,
    droppable = true,
    index,
    onBoardRearrange,
    supportedBoardDragTypes,
    ...restOfProps
  }: WorkspaceSidebarBoardsListItemProps) => {
    const [isRenaming, setIsRenaming] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const open = useWorkspaceSidebarSectionStore((store) => !!store.getTreeItem({ id: board.id })?.open);
    const touched = useWorkspaceSidebarSectionStore((store) => !!store.getTreeItem({ id: board.id })?.touched);
    const setTreeItem = useWorkspaceSidebarSectionStore((state) => state.setTreeItem);
    const toggleTreeItemOpen = useWorkspaceSidebarSectionStore((state) => state.toggleTreeItemOpen);
    const currentBoardId = useURLBoardIdSelector();
    const { boardPermissions } = useBoardPermissions({
      boardId: board.id,
    });
    const {
      data: subBoards = [],
      isInitialLoading,
      isLoadingMore,
      hasMore,
      loadNextPage,
    } = useSubBoardsList({ boardId: board.id, canFetchData: open || false, libraryId: board.library?.id });
    const { createSubBoard } = useCreateSubBoard({
      enableTracking: false,
    });
    const { trackSidebarInteraction } = useTrackSidebarInteraction();
    const isAncestorOfCurrentBoard = useAirSelector((state) => isAncestorOfCentralizedBoardSelector(state, board.id));
    const { onBoardLinkClick } = usePrivateBoardLinkClick({ board });

    const isBoardActive = currentBoardId === board.id;

    const isCreateBoardButtonVisible = useMemo(() => {
      return canCreateBoard(boardPermissions) && open && subBoards.length === 0 && !isInitialLoading && !isLoadingMore;
    }, [boardPermissions, isInitialLoading, isLoadingMore, open, subBoards.length]);

    useEffect(() => {
      if (touched) {
        return;
      }

      if (isAncestorOfCurrentBoard) {
        setTreeItem({ id: board.id, open: true, touched: false });
      }
    }, [board.id, isAncestorOfCurrentBoard, setTreeItem, touched]);

    useFetchObjectsPermissions({
      objects: {
        boardIds: subBoards.map((board) => board.id),
      },
    });

    const onToggleOpen = useCallback(() => {
      toggleTreeItemOpen({ id: board.id });

      trackSidebarInteraction({
        action: open ? 'collapse' : 'expand',
        type: 'board',
      });
    }, [board.id, trackSidebarInteraction, toggleTreeItemOpen, open]);

    const getCanRearrange = useCallback(() => {
      // For subboards, check if user can rearrange within the parent board
      return canDeleteBoard(boardPermissions) || canChangeBoardAssetsOrBoardsPosition(boardPermissions);
    }, [boardPermissions]);

    const onLinkClick = useCallback(
      (event: MouseEvent<HTMLAnchorElement>) => {
        onBoardLinkClick(event);
        trackSidebarInteraction({
          action: 'link_click',
          type: 'board',
        });
      },
      [trackSidebarInteraction, onBoardLinkClick],
    );

    const onCreateSubBoard = useCallback(() => {
      createSubBoard({
        selectedParentBoard: board,
        trackLocation: 'side-nav-extension-item-sub-button',
      });
      trackSidebarInteraction({
        action: 'create_board',
        type: 'board',
      });
    }, [board, createSubBoard, trackSidebarInteraction]);

    useEffect(() => {
      /**
       * If the board has been touched, we don't want to auto-expand it.
       */
      if (touched) {
        return;
      }

      /**
       * If the board is an ancestor of the current board and autoExpand is true,
       * we need to open the board.
       */
      if (isAncestorOfCurrentBoard && autoExpand) {
        setTreeItem({ id: board.id, open: true });
      }
    }, [board.id, autoExpand, isAncestorOfCurrentBoard, touched, setTreeItem]);

    const { handleOpenChange: handleDropdownLockChange } = useWorkspaceSidebarPushLock({
      token: `sidebar-board-dropdown-${board.id}`,
    });

    const { handleOpenChange: handleRenameLockChange } = useWorkspaceSidebarPushLock({
      token: `sidebar-board-rename-${board.id}`,
    });

    const onDropdownOpenChange = useCallback<Required<DropdownMenuProps>['onOpenChange']>(
      (open) => {
        setIsDropdownOpen(open);
        handleDropdownLockChange(open);

        if (open) {
          trackSidebarInteraction({
            action: 'open_sub_menu',
            type: 'board',
          });
        }
      },
      [handleDropdownLockChange, trackSidebarInteraction],
    );

    const onRenamingStart = useCallback(() => {
      handleRenameLockChange(true);
      setIsRenaming(true);
    }, [handleRenameLockChange]);

    const onRenamingEnd = useCallback(() => {
      handleRenameLockChange(false);
      setIsRenaming(false);
    }, [handleRenameLockChange]);

    return (
      <DNDBoardDrag
        className="data-[dnd-dragging=true]:opacity-50 data-[dnd-moving=true]:opacity-50"
        id={board.id}
        item={{
          type: dragType,
          board,
        }}
        index={index}
        enabled={draggable}
      >
        <DNDBoardDrop
          asChild
          dropLocation="board"
          board={board}
          enabled={droppable}
          supportedBoardDragTypes={supportedBoardDragTypes}
        >
          <div
            className="group relative flex h-8 cursor-pointer items-center gap-2 rounded-8 px-2 font-normal text-14 text-grey-11 outline-none transition-colors hover:bg-grey-5 focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset active:bg-grey-5 data-[dnd-over=true]:data-[dnd-droppable=true]:z-10 data-[dnd-over=true]:data-[dnd-droppable=true]:ring-2 data-[dnd-over=true]:data-[dnd-droppable=true]:ring-blue-9 data-[active=true]:bg-grey-5 data-[menu-open=true]:bg-grey-5"
            data-testid="WORKSPACE_SIDEBAR_BOARDS_LIST_ITEM"
            data-title={board.title}
            data-active={isBoardActive}
            data-menu-open={isDropdownOpen}
            data-open={open}
            style={{
              paddingLeft: Math.max(
                depth * WORKSPACE_SIDEBAR_BOARD_ITEM_DEPTH_MULTIPLIER,
                WORKSPACE_SIDEBAR_BOARD_ITEM_BASE_PADDING_LEFT,
              ),
            }}
            {...restOfProps}
          >
            <Link
              className="absolute inset-0 z-30 size-full"
              data-testid="WORKSPACE_SIDEBAR_BOARD_LINK"
              href={createBoardRoute(board.id, board.title)}
              onClick={onLinkClick}
            >
              <VisuallyHidden>Go to {board.title}</VisuallyHidden>
            </Link>

            <WorkspaceSidebarBoardsListItemButton
              className="group relative z-40 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="WORKSPACE_SIDEBAR_BOARDS_LIST_ITEM_EXPAND_BUTTON"
              onClick={onToggleOpen}
            >
              <TriangleDown className="size-4 transition-transform group-data-[open=false]:-rotate-90 group-data-[open=true]:rotate-0" />
            </WorkspaceSidebarBoardsListItemButton>

            {isRenaming ? (
              <WorkspaceSidebarBoardsListItemRenameInput
                board={board}
                onCancel={onRenamingEnd}
                onSubmit={onRenamingEnd}
              />
            ) : (
              <span className="grow truncate">{board.title}</span>
            )}
            <WorkspacesSidebarBoardsListItemActions
              board={board}
              isDropdownOpen={isDropdownOpen}
              onDropdownOpenChange={onDropdownOpenChange}
              onRenamingStart={onRenamingStart}
            />
          </div>
        </DNDBoardDrop>

        {open && !!subBoards.length && (
          <WorkspaceSidebarBoardsList
            boards={subBoards}
            depth={depth + 1}
            dragType={dragType}
            draggable={draggable}
            droppable={droppable}
            /**
             * We can hardcode this permission check because we know the user is inside of a sub-board so
             * it's the parent board's permissions we need to check
             */
            getCanRearrange={getCanRearrange}
            onBoardRearrange={onBoardRearrange}
            parentBoardId={board.id}
            supportedBoardDragTypes={supportedBoardDragTypes}
          />
        )}
        {hasMore && open && (
          <Button
            appearance="ghost"
            className="justify-start"
            color="grey"
            isLoading={isLoadingMore}
            onClick={() => loadNextPage()}
            size="small"
            style={{
              marginLeft: Math.max(
                depth * WORKSPACE_SIDEBAR_BOARD_ITEM_DEPTH_MULTIPLIER +
                  WORKSPACE_SIDEBAR_BOARD_ITEM_EXPAND_BUTTON_SIZE +
                  WORKSPACE_SIDEBAR_BOARD_ITEM_GAP,
                WORKSPACE_SIDEBAR_BOARD_ITEM_BASE_PADDING_LEFT,
              ),
            }}
          >
            Show more
          </Button>
        )}
        {isCreateBoardButtonVisible && (
          <Button
            appearance="ghost"
            className="my-1"
            color="grey"
            onClick={onCreateSubBoard}
            size="small"
            style={{
              marginLeft: Math.max(
                depth * WORKSPACE_SIDEBAR_BOARD_ITEM_DEPTH_MULTIPLIER +
                  WORKSPACE_SIDEBAR_BOARD_ITEM_EXPAND_BUTTON_SIZE +
                  WORKSPACE_SIDEBAR_BOARD_ITEM_GAP,
                WORKSPACE_SIDEBAR_BOARD_ITEM_BASE_PADDING_LEFT,
              ),
            }}
          >
            <span className="truncate">Create new sub-board</span>
          </Button>
        )}
      </DNDBoardDrag>
    );
  },
);

WorkspaceSidebarBoardsListItem.displayName = 'WorkspaceSidebarBoardsListItem';
